import React, { createContext, useContext, useState, useEffect } from "react";
import { CookieSettings } from "@/types/cookie";

interface CookieContextType {
  isOpen: boolean;
  cookieSettings: CookieSettings;
  openBanner: () => void;
  closeBanner: () => void;
  updateCookieSettings: (settings: CookieSettings) => void;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem("cookieSettings");
    const consentGiven = localStorage.getItem("cookieConsent");

    if (savedSettings) {
      setCookieSettings(JSON.parse(savedSettings));
    }

    if (!consentGiven) {
      setIsOpen(true);
    }
  }, []);

  const openBanner = () => setIsOpen(true);
  const closeBanner = () => setIsOpen(false);

  const updateCookieSettings = (settings: CookieSettings) => {
    setCookieSettings(settings);
    localStorage.setItem("cookieSettings", JSON.stringify(settings));
  };

  return (
    <CookieContext.Provider
      value={{
        isOpen,
        cookieSettings,
        openBanner,
        closeBanner,
        updateCookieSettings,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
};

export const useCookieContext = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error("useCookieContext must be used within a CookieProvider");
  }
  return context;
};
